import React, { useState, useEffect } from 'react';
import { Box, TextField, MenuItem, Slider, Typography, Paper, Grid } from '@mui/material';

const StructuredDataInput = ({ formData, updateFormData, onValidationChange }) => {
  const [localData, setLocalData] = useState({
    age: formData.age || '',
    incomeTrajectory: formData.incomeTrajectory || '',
    relationshipStatus: formData.relationshipStatus || '',
    weeklyExerciseHours: formData.weeklyExerciseHours || 0,
    sleepDebtHours: formData.sleepDebtHours || 0,
    biggestExpense: formData.biggestExpense || '',
    photoDescription: formData.photoDescription || ''
  });

  const [errors, setErrors] = useState({});

  // Validate on mount and whenever data changes
  useEffect(() => {
    validateForm();
  }, [localData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!localData.age || localData.age < 18 || localData.age > 100) {
      newErrors.age = 'Age must be between 18 and 100';
    }
    
    if (!localData.incomeTrajectory) {
      newErrors.incomeTrajectory = 'Please select your income trajectory';
    }
    
    if (!localData.relationshipStatus) {
      newErrors.relationshipStatus = 'Please select your relationship status';
    }
    
    if (!localData.biggestExpense) {
      newErrors.biggestExpense = 'Please select your biggest expense';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    onValidationChange(isValid);
    
    return isValid;
  };

  const handleChange = (field, value) => {
    const updatedData = { ...localData, [field]: value };
    setLocalData(updatedData);
    updateFormData(updatedData);
  };

  // Cyber Noir themed text field styling
  const getTextFieldStyles = (hasError) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1F2833',
      color: '#EDEDED',
      fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      '& fieldset': { 
        borderColor: hasError ? '#00FFF5' : 'rgba(0,255,245,0.3)',
        borderWidth: hasError ? '2px' : '2px',
        transition: 'all 0.3s ease',
        boxShadow: hasError ? '0 0 10px rgba(0,255,245,0.5), inset 0 0 5px rgba(0,255,245,0.2)' : 'none',
      },
      '&:hover fieldset': { 
        borderColor: hasError ? '#00FFF5' : '#00FFF5',
        boxShadow: hasError ? '0 0 15px rgba(0,255,245,0.6), inset 0 0 5px rgba(0,255,245,0.25)' : '0 0 8px rgba(0,255,245,0.4)',
      },
      '&.Mui-focused fieldset': { 
        borderColor: '#9D00FF',
        borderWidth: '2px',
        boxShadow: '0 0 15px rgba(157,0,255,0.5), inset 0 0 5px rgba(157,0,255,0.2)',
      },
      '&.Mui-error fieldset': {
        borderColor: '#00FFF5 !important',
        borderWidth: '2px !important',
        boxShadow: '0 0 15px rgba(0,255,245,0.7), inset 0 0 8px rgba(0,255,245,0.25) !important',
      }
    },
    '& .MuiInputLabel-root': { 
      color: hasError ? '#00FFF5' : '#B0B0B0',
      fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      fontWeight: 500,
      textShadow: hasError ? '0 0 10px rgba(0,255,245,0.5)' : 'none',
      '&.Mui-focused': {
        color: '#9D00FF',
        textShadow: '0 0 10px rgba(157,0,255,0.5)',
      },
      '&.Mui-error': {
        color: '#00FFF5',
        textShadow: '0 0 10px rgba(0,255,245,0.6)',
      }
    },
    '& .MuiFormHelperText-root': { 
      color: hasError ? '#00FFF5' : '#B0B0B0',
      fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '0.75rem',
      fontWeight: hasError ? 600 : 400,
      textShadow: hasError ? '0 0 8px rgba(0,255,245,0.4)' : 'none',
      '&.Mui-error': {
        color: '#00FFF5',
        fontWeight: 600,
        textShadow: '0 0 8px rgba(0,255,245,0.5)',
      }
    },
    '& .MuiSelect-icon': {
      color: hasError ? '#00FFF5' : '#B0B0B0',
    }
  });

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ 
        fontWeight: 600, 
        mb: 3,
        color: '#EDEDED',
        background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}>
        Step 1: Structured Data
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: '#B0B0B0' }}>
        These 5-7 data points give us the structural baseline for your forecast.
      </Typography>

      <Grid container spacing={3}>
        {/* Age */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Age"
            type="number"
            value={localData.age}
            onChange={(e) => handleChange('age', parseInt(e.target.value) || '')}
            error={!!errors.age}
            helperText={errors.age || 'Age must be between 18 and 100'}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 18, max: 100 }}
            sx={getTextFieldStyles(!!errors.age)}
          />
        </Grid>

        {/* Income Trajectory */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Income Trajectory"
            value={localData.incomeTrajectory}
            onChange={(e) => handleChange('incomeTrajectory', e.target.value)}
            error={!!errors.incomeTrajectory}
            helperText={errors.incomeTrajectory || 'Please select your income trajectory'}
            variant="outlined"
            sx={getTextFieldStyles(!!errors.incomeTrajectory)}
          >
            <MenuItem value="increasing">Increasing</MenuItem>
            <MenuItem value="stable">Stable</MenuItem>
            <MenuItem value="declining">Declining</MenuItem>
            <MenuItem value="volatile">Volatile</MenuItem>
          </TextField>
        </Grid>

        {/* Relationship Status */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Relationship Status"
            value={localData.relationshipStatus}
            onChange={(e) => handleChange('relationshipStatus', e.target.value)}
            error={!!errors.relationshipStatus}
            helperText={errors.relationshipStatus || 'Please select your relationship status'}
            variant="outlined"
            sx={getTextFieldStyles(!!errors.relationshipStatus)}
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="partnered">Partnered</MenuItem>
            <MenuItem value="complicated">Complicated</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>

        {/* Biggest Expense */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Biggest Recurring Expense"
            value={localData.biggestExpense}
            onChange={(e) => handleChange('biggestExpense', e.target.value)}
            error={!!errors.biggestExpense}
            helperText={errors.biggestExpense || 'Please select your biggest expense'}
            variant="outlined"
            sx={getTextFieldStyles(!!errors.biggestExpense)}
          >
            <MenuItem value="rent">Rent</MenuItem>
            <MenuItem value="mortgage">Mortgage</MenuItem>
            <MenuItem value="food">Food</MenuItem>
            <MenuItem value="transport">Transportation</MenuItem>
            <MenuItem value="entertainment">Entertainment</MenuItem>
            <MenuItem value="debt">Debt Payments</MenuItem>
            <MenuItem value="healthcare">Healthcare</MenuItem>
            <MenuItem value="education">Education</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>

        {/* Weekly Exercise */}
        <Grid item xs={12}>
          <Typography gutterBottom sx={{ 
            color: '#B0B0B0', 
            fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif', 
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            Weekly Exercise Hours: {localData.weeklyExerciseHours}
          </Typography>
          <Slider
            value={localData.weeklyExerciseHours}
            onChange={(e, value) => handleChange('weeklyExerciseHours', value)}
            min={0}
            max={20}
            marks={[
              { value: 0, label: '0' },
              { value: 5, label: '5' },
              { value: 10, label: '10' },
              { value: 15, label: '15' },
              { value: 20, label: '20+' }
            ]}
            valueLabelDisplay="auto"
            sx={{
              color: '#00FFF5',
              '& .MuiSlider-thumb': { 
                backgroundColor: '#00FFF5',
                boxShadow: '0 0 10px rgba(0,255,245,0.6)',
                '&:hover': {
                  boxShadow: '0 0 15px rgba(0,255,245,0.8)',
                }
              },
              '& .MuiSlider-track': { 
                backgroundColor: '#00FFF5',
                border: 'none',
                boxShadow: '0 0 5px rgba(0,255,245,0.4)',
              },
              '& .MuiSlider-rail': { backgroundColor: '#2A3640' },
              '& .MuiSlider-mark': { backgroundColor: '#2A3640' },
              '& .MuiSlider-markLabel': { 
                color: '#B0B0B0', 
                fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif', 
                fontSize: '0.75rem' 
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: '#00FFF5',
                color: '#0B0C10',
                fontWeight: 600,
              }
            }}
          />
        </Grid>

        {/* Sleep Debt */}
        <Grid item xs={12}>
          <Typography gutterBottom sx={{ 
            color: '#B0B0B0', 
            fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif', 
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            Sleep Debt Hours per Week: {localData.sleepDebtHours}
          </Typography>
          <Slider
            value={localData.sleepDebtHours}
            onChange={(e, value) => handleChange('sleepDebtHours', value)}
            min={0}
            max={20}
            marks={[
              { value: 0, label: '0' },
              { value: 5, label: '5' },
              { value: 10, label: '10' },
              { value: 15, label: '15' },
              { value: 20, label: '20+' }
            ]}
            valueLabelDisplay="auto"
            sx={{
              color: '#00FFF5',
              '& .MuiSlider-thumb': { 
                backgroundColor: '#00FFF5',
                boxShadow: '0 0 10px rgba(0,255,245,0.6)',
                '&:hover': {
                  boxShadow: '0 0 15px rgba(0,255,245,0.8)',
                }
              },
              '& .MuiSlider-track': { 
                backgroundColor: '#00FFF5',
                border: 'none',
                boxShadow: '0 0 5px rgba(0,255,245,0.4)',
              },
              '& .MuiSlider-rail': { backgroundColor: '#2A3640' },
              '& .MuiSlider-mark': { backgroundColor: '#2A3640' },
              '& .MuiSlider-markLabel': { 
                color: '#B0B0B0', 
                fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif', 
                fontSize: '0.75rem' 
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: '#00FFF5',
                color: '#0B0C10',
                fontWeight: 600,
              }
            }}
          />
          <Typography variant="caption" sx={{ 
            color: '#B0B0B0', 
            fontStyle: 'italic', 
            display: 'block', 
            mt: 1 
          }}>
            How many hours of sleep are you missing each week?
          </Typography>
        </Grid>

        {/* Photo Description (Optional) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Workspace Description (Optional)"
            value={localData.photoDescription}
            onChange={(e) => handleChange('photoDescription', e.target.value)}
            helperText="Describe your desk, workspace, or living environment. This is optional but adds context."
            variant="outlined"
            sx={getTextFieldStyles(false)}
          />
        </Grid>
      </Grid>

      <Paper sx={{ 
        mt: 4, 
        p: 3, 
        backgroundColor: 'rgba(0,255,245,0.05)', 
        border: '1px solid rgba(0,255,245,0.2)',
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0,255,245,0.1)',
      }}>
        <Typography variant="body2" sx={{ 
          color: '#B0B0B0', 
          fontStyle: 'italic',
          fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          <strong style={{ color: '#00FFF5', textShadow: '0 0 5px rgba(0,255,245,0.3)' }}>Privacy note:</strong> This data is used only for your forecast analysis. We never share or sell your information.
        </Typography>
      </Paper>
    </Box>
  );
};

export default StructuredDataInput;
